import React, { useState } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Icon from '../../../components/AppIcon';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const inputStyle = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#fff',
  borderRadius: '6px',
  padding: '2px 4px',
  fontSize: '11px',
  textAlign: 'center',
  outline: 'none',
};

const SortableWorkoutItem = ({ item, onRemove, onUpdate }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item?.id });
  const style = { transform: CSS?.Transform?.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className="flex items-center gap-2 p-2 rounded-lg group transition-all"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none flex-shrink-0">
          <Icon name="GripVertical" size={14} style={{ color: 'rgba(255,255,255,0.25)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>{item?.name}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <input
              type="number"
              value={item?.sets}
              onChange={e => onUpdate(item?.id, 'sets', parseInt(e?.target?.value) || 1)}
              style={{ ...inputStyle, width: '36px' }}
              min="1"
            />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>×</span>
            <input
              type="number"
              value={item?.reps}
              onChange={e => onUpdate(item?.id, 'reps', parseInt(e?.target?.value) || 1)}
              style={{ ...inputStyle, width: '36px' }}
              min="1"
            />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>reps</span>
            <input
              type="number"
              value={item?.restSeconds}
              onChange={e => onUpdate(item?.id, 'restSeconds', parseInt(e?.target?.value) || 30)}
              style={{ ...inputStyle, width: '44px' }}
              min="0"
            />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>s</span>
          </div>
        </div>
        <button
          onClick={() => onRemove(item?.id)}
          className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
          style={{ color: 'rgba(255,68,68,0.7)', background: 'rgba(255,68,68,0.1)' }}
        >
          <Icon name="X" size={12} />
        </button>
      </div>
    </div>
  );
};

const DayColumn = ({ day, dayIndex, items, onDrop, onRemoveItem, onUpdateItem, isRestDay, onToggleRest }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const oldIndex = items?.findIndex(i => i?.id === active?.id);
      const newIndex = items?.findIndex(i => i?.id === over?.id);
      onDrop(dayIndex, arrayMove(items, oldIndex, newIndex));
    }
  };

  const totalDuration = items?.reduce((acc, item) => {
    return acc + (item?.sets * item?.reps * 3) + (item?.restSeconds * (item?.sets - 1));
  }, 0);

  return (
    <div
      className="flex flex-col rounded-xl transition-all min-h-[280px]"
      style={{
        background: isRestDay ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
        border: isRestDay ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div>
          <p className="text-xs font-bold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>{day}</p>
          {!isRestDay && items?.length > 0 && (
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{items?.length} ex · {Math.ceil(totalDuration / 60)}min</p>
          )}
        </div>
        <button
          onClick={() => onToggleRest(dayIndex)}
          className="text-xs px-2 py-0.5 rounded-full font-medium transition-all"
          style={{
            background: isRestDay ? 'rgba(255,255,255,0.06)' : 'rgba(170,255,0,0.1)',
            color: isRestDay ? 'rgba(255,255,255,0.35)' : '#AAFF00',
            border: isRestDay ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(170,255,0,0.25)',
            fontFamily: "'Inter Tight', 'Inter', sans-serif",
          }}
        >
          {isRestDay ? 'Rest' : 'Active'}
        </button>
      </div>

      {isRestDay ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Moon" size={20} className="mx-auto mb-1" style={{ color: 'rgba(255,255,255,0.2)' }} />
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>Rest Day</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 p-2 space-y-1.5 overflow-y-auto">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items?.map(i => i?.id)} strategy={verticalListSortingStrategy}>
              {items?.map(item => (
                <SortableWorkoutItem
                  key={item?.id}
                  item={item}
                  onRemove={(id) => onRemoveItem(dayIndex, id)}
                  onUpdate={(id, field, val) => onUpdateItem(dayIndex, id, field, val)}
                />
              ))}
            </SortableContext>
          </DndContext>
          {items?.length === 0 && (
            <div className="flex-1 flex items-center justify-center py-6 rounded-lg" style={{ border: '2px dashed rgba(255,255,255,0.08)' }}>
              <p className="text-xs text-center" style={{ color: 'rgba(255,255,255,0.25)' }}>Drag exercises here</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const WeeklyProgramTimeline = ({ weekDays, onDropToDay, onRemoveFromDay, onUpdateItem, onToggleRestDay }) => {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)' }}>
          <Icon name="Calendar" size={16} style={{ color: '#00D4FF' }} />
        </div>
        <h2 className="text-base font-bold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Weekly Program</h2>
        <span className="ml-auto text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
          {weekDays?.reduce((acc, d) => acc + d?.exercises?.length, 0)} total exercises
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 gap-3">
          {weekDays?.map((day, idx) => (
            <DayColumn
              key={day?.name}
              day={day?.name}
              dayIndex={idx}
              items={day?.exercises}
              onDrop={onDropToDay}
              onRemoveItem={onRemoveFromDay}
              onUpdateItem={onUpdateItem}
              isRestDay={day?.isRest}
              onToggleRest={onToggleRestDay}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeeklyProgramTimeline;
